import React, { useEffect, useState } from 'react'
import * as S from './styles'
import TextFieldNew from 'presentation/shared/components/TextFieldNew'
import ButtonNew from 'presentation/shared/components/ButtonNew'
import SelectFieldNew from 'presentation/shared/components/SelectFieldNew'
import { HealthInsurancePlans } from 'domain/usecases/health-insurance/load-health-insurance-plans'
import Table from 'presentation/shared/components/Table'
import { useServices } from 'presentation/hooks/use-services'
import { CheckIntegrationOvermind } from 'domain/usecases/eligibility/check-integration-overmind'
import { toast } from 'react-toastify'
import RadioButtonNew from 'presentation/shared/components/RadioButtonNew'
import { FindConsultsEligibility } from 'domain/usecases/eligibility/find-consults-eligibility'
import moment from 'moment'
import { HealthInsuranceEnum, TranslatedHealthInsuranceEnum } from '../../types'
import { TooltipNew } from 'presentation/shared/components/TooltipNew'
import { ReactComponent as InfoIcon } from 'presentation/assets/icons/info-icon.svg'
import { convertNameToTitleCase } from '../../functions'
import { LoadSurgicalOrderNoticeCard } from 'domain/usecases/surgical-pendency/load-surgical-order-notice-card'
import LoadingModal from 'presentation/shared/components/LoadingModal'
import EligibilityDocument from 'presentation/hospital/components/EligibilityDocument'

type Props = {
  availableHealthInsurances: any
  surgicalOrderNoticeCard?: LoadSurgicalOrderNoticeCard.Model
  loadSurgicalOrderNoticeCard: () => void
  setIsLoading: any
  isLoading?: boolean
}

export enum HealthInsuranceIntegrationOvermindReasonEnum {
  NOT_FOUND = 'NOT_FOUND_IN_OVERMIND',
  IS_PARTICULAR = 'IS_PARTICULAR',
  INTEGRATED = 'INTEGRATED'
}

function getEligibilityStatus(value: string) {
  switch (value) {
    case HealthInsuranceEnum.ELIGIBLE:
      return {
        title: 'Elegível',
        color: '#4CAF50'
      }
    case HealthInsuranceEnum.ELIGIBLE_PENDING:
      return {
        title: 'Elegibilidade pendente',
        color: '#FFC107'
      }
    case HealthInsuranceEnum.INELIGIBLE:
      return {
        title: 'Não elegível',
        color: '#D32F2F'
      }
  }
}

export const eligibilityColumns = [
  {
    name: 'patient_name',
    label: 'Nome',
    options: { filter: false, sort: false }
  },
  {
    name: 'health_card',
    label: 'N° Carteirinha',
    options: { filter: false, sort: false }
  },
  {
    name: 'health_insurance_name',
    label: 'Convênio',
    options: {
      filter: true,
      sort: false
    }
  },
  {
    name: 'overmind_plan_name',
    label: 'Plano',
    options: {
      filter: true,
      sort: false
    }
  },
  {
    name: 'overmind_sub_plan_name',
    label: 'Subplano',
    options: {
      filter: true,
      sort: false
    }
  },
  {
    name: 'unit',
    label: 'Unidade',
    options: {
      filter: true,
      sort: false
    }
  },
  {
    name: 'status',
    label: 'Elegibilidade do Convênio',
    options: {
      filter: true,
      sort: false,
      // eslint-disable-next-line react/display-name
      customBodyRender: (value: any) => (
        <div>
          <TooltipNew text={value.reason}>
            <S.TitleEligibility
              color={getEligibilityStatus(value.status)?.color}
            >
              <InfoIcon />
              {getEligibilityStatus(value.status)?.title}
            </S.TitleEligibility>
          </TooltipNew>

          <S.CustomDescription>
            {value.created_at &&
              moment.utc(value.created_at).format('DD/MM/YYYY - HH[h]mm')}
          </S.CustomDescription>
          <S.CustomDescription>{value.requested_by}</S.CustomDescription>
        </div>
      )
    }
  },
  {
    name: 'eligibility_documentation',
    label: 'Documentação',
    options: {
      filter: true,
      sort: false,
      // eslint-disable-next-line react/display-name
      customBodyRender: (value: any) =>
        value.file_name !== null ? (
          <EligibilityDocument
            file_name={value.file_name}
            health_insurance_status_id={value.health_insurance_status_id}
            documentations={value.documents[0]}
            eligibilityStatus={value.status}
            findConsultsEligibility={value.findConsultsEligibility}
          />
        ) : (
          <p>Sem Anexos</p>
        )
    }
  }
]

export const EligibilityTab = ({
  surgicalOrderNoticeCard,
  availableHealthInsurances,
  loadSurgicalOrderNoticeCard,
  setIsLoading,
  isLoading
}: Props) => {
  const healthInsurancesList = () => {
    return availableHealthInsurances?.map((i: HealthInsurancePlans) => ({
      label: i?.description,
      value: Number(i?.code)
    }))
  }

  const [integrationOvermindInfo, setIntegrationOvermindInfo] =
    useState<CheckIntegrationOvermind.Model>()

  const [consultsEligibility, setConsultsEligibility] =
    useState<FindConsultsEligibility.Model>()

  const [consultMessage, setConsultMessage] = useState('')
  const eligibilityService = useServices().eligibility
  const [disabledHealthInsuranceCard, setDisabledHealthInsuranceCard] =
    useState(false)

  const [manualStatusEligibility, setManualStatusEligibility] =
    useState<HealthInsuranceEnum>()

  async function checkIntegrationOvermind(surgical_order_id: number) {
    try {
      setIsLoading(true)
      const response = await eligibilityService.checkIntegrationOvermind(
        surgical_order_id
      )
      setIntegrationOvermindInfo(response)

      setIsLoading(false)
    } catch (error) {
      setIsLoading(false)
      toast.error('Erro na verificação da elegibilidade. Tente novamente.')
    }
  }

  async function createEligibility(eligibilityStatus: any) {
    const surgicalOrderId =
      surgicalOrderNoticeCard?.data?.data?.card?.surgical_order_id

    try {
      if (surgicalOrderId) {
        setIsLoading(true)
        await eligibilityService.createEligibility({
          surgical_order_id: surgicalOrderId,
          status: eligibilityStatus
        })
        setIsLoading(false)
        await findConsultsEligibility(surgicalOrderId)
        await loadSurgicalOrderNoticeCard()
        toast.success('Elegibilidade manual registrada')
      }
    } catch (error) {
      setIsLoading(false)
      return toast.error('Ocorreu um erro no registro de elegibilidade manual')
    }
  }

  async function createConsultEligibility() {
    const surgicalOrderId =
      surgicalOrderNoticeCard?.data?.data?.card?.surgical_order_id
    const healthCard = integrationOvermindInfo?.data.data.health_card
    const healthInsuranceName =
      surgicalOrderNoticeCard?.data?.data?.card?.health_insurance.name

    try {
      setIsLoading(true)

      if (surgicalOrderId && healthCard && healthInsuranceName) {
        const response = await eligibilityService.createConsultEligibility({
          surgical_order_id: surgicalOrderId,
          health_card: healthCard,
          health_insurance_name: healthInsuranceName
        })

        if (response.statusCode === 201) {
          await checkIntegrationOvermind(surgicalOrderId)
          await findConsultsEligibility(surgicalOrderId)
          await loadSurgicalOrderNoticeCard()
          return toast.success('Elegibilidade criada com sucesso.')
        }

        if (
          response?.message ===
          'Carteirinha inválida. Edite no resumo do pedido.'
        ) {
          setConsultMessage(response?.message)
          setDisabledHealthInsuranceCard(true)
          return toast.error('Carteirinha inválida. Edite no resumo do pedido.')
        }

        if (
          response?.message ===
          'Erro na verificação da elegibilidade. Tente novamente.'
        ) {
          return toast.error(
            'Erro na verificação da elegibilidade. Tente novamente.'
          )
        }

        if (response?.statusCode === 400) {
          return toast.error(response?.message)
        }
      } else return
    } catch (error) {
      toast.error('Erro na verificação da elegibilidade. Tente novamente.')
    } finally {
      setIsLoading(false)
    }
  }

  async function findConsultsEligibility(surgical_order_id: number) {
    try {
      setIsLoading(true)
      const response = await eligibilityService.findConsultsEligibility(
        surgical_order_id
      )

      setConsultsEligibility(response)
      setIsLoading(false)
    } catch (error) {
      setIsLoading(false)
      toast.error(
        'Erro na busca de consultas de elegibilidade. Tente novamente.'
      )
    }
  }

  useEffect(() => {
    if (surgicalOrderNoticeCard) {
      checkIntegrationOvermind(
        surgicalOrderNoticeCard?.data?.data?.card?.surgical_order_id
      )
      findConsultsEligibility(
        surgicalOrderNoticeCard?.data?.data?.card?.surgical_order_id
      )
    }
  }, [])

  const hasInsuranceCardNumber =
    surgicalOrderNoticeCard?.data?.data?.card?.health_insurance?.health_card ||
    integrationOvermindInfo?.data.data.health_card

  const formatedConsults =
    consultsEligibility?.data.data?.health_insurance_status.map((item) => {
      return {
        ...item,
        status: {
          status: item.status,
          reason: item.reason,
          requested_by: item.requested_by,
          created_at: item.created_at
        },
        eligibility_documentation: {
          surgical_order_id:
            surgicalOrderNoticeCard?.data?.data?.card?.surgical_order_id,
          health_insurance_status_id: item.id,
          file_name: item.file_name,
          documents: item.documentations,
          status: item.status,
          findConsultsEligibility: () =>
            findConsultsEligibility(
              surgicalOrderNoticeCard!.data.data.card.surgical_order_id
            )
        },
        health_card: item.health_card,
        health_insurance_name:
          consultsEligibility.data.data?.health_insurance_name,
        unit: surgicalOrderNoticeCard?.data?.data?.card?.hospital.name,
        patient_name: item.patient_name
      }
    })

  function existingOvermindIntegration() {
    return (
      integrationOvermindInfo?.data?.data?.integration_overmind[0]
        ?.is_integrated === true &&
      integrationOvermindInfo?.data?.data?.integration_overmind[0]?.reason ===
        HealthInsuranceIntegrationOvermindReasonEnum.INTEGRATED
    )
  }

  {
    if (
      integrationOvermindInfo?.data?.data?.integration_overmind[0]?.reason ===
      HealthInsuranceIntegrationOvermindReasonEnum.IS_PARTICULAR
    ) {
      return (
        <S.CenterContainer style={{ marginRight: '30px' }}>
          <S.TextStrong>
            Pedido particular. Não requer verificação de elegibilidade.
          </S.TextStrong>
        </S.CenterContainer>
      )
    }

    if (
      integrationOvermindInfo?.data?.data?.integration_overmind[0]
        ?.is_integrated === false
    ) {
      return (
        <S.CenterContainer style={{ marginLeft: '30px' }}>
          <S.TextStrong>
            O convênio informado ainda não possui verificação de elegibilidade
            automatizada. Consulte a elegibilidade no canal oficial do convênio
            e registre abaixo.
          </S.TextStrong>

          <S.TextLight>Informe o status do elegibilidade:</S.TextLight>

          <S.RadioContainer>
            <RadioButtonNew
              showBackground={false}
              name="eligibility_status"
              labelFor="Elegível"
              id="Elegível"
              label="Elegível"
              onCheck={() =>
                setManualStatusEligibility(HealthInsuranceEnum.ELIGIBLE)
              }
            />

            <RadioButtonNew
              showBackground={false}
              name="eligibility_status"
              labelFor="Não elegível"
              id="Não elegível"
              label="Não elegível"
              onCheck={() =>
                setManualStatusEligibility(HealthInsuranceEnum.INELIGIBLE)
              }
            />
          </S.RadioContainer>
          <S.ButtonContainer>
            <ButtonNew
              onClick={() => createEligibility(manualStatusEligibility)}
            >
              Salvar
            </ButtonNew>
          </S.ButtonContainer>

          {consultsEligibility?.data.data.health_insurance_status?.map(
            (item, index) => {
              return (
                item.requested_by &&
                item.created_at && (
                  <>
                    <S.ManualStatusInfo key={index}>
                      {(TranslatedHealthInsuranceEnum as any)[item.status] +
                        ' '}
                      - Status registrado por
                      {' ' + convertNameToTitleCase(item.requested_by) + ' '}
                      em{' '}
                      {moment
                        .utc(item.created_at)
                        .format('DD/MM/YYYY [às] HH:mm')}
                    </S.ManualStatusInfo>
                  </>
                )
              )
            }
          )}
        </S.CenterContainer>
      )
    } else if (formatedConsults && integrationOvermindInfo) {
      return (
        <>
          <S.CenterContainer style={{ marginLeft: '30px' }}>
            <S.StrongTitle>Histórico de verificação </S.StrongTitle>
            {surgicalOrderNoticeCard?.data?.data?.card?.health_insurance
              .health_insurance_status[0].status ===
              HealthInsuranceEnum.ELIGIBLE_PENDING ||
            !consultsEligibility?.data.data?.health_insurance_status.length ? (
              <S.TextDescription>
                Nenhuma consulta de elegibilidade foi realizada por enquanto.
                Verifique a elegibilidade na coluna à direita.
              </S.TextDescription>
            ) : (
              <Table
                columns={eligibilityColumns}
                data={formatedConsults}
                pagination={false}
              />
            )}
          </S.CenterContainer>

          {existingOvermindIntegration() && (
            <>
              <S.Separator />
              <S.RightContainer>
                <S.StrongTitle>Dados do paciente </S.StrongTitle>
                <S.PatientControlPanel>
                  <TextFieldNew
                    name="healthInsuranceCard"
                    placeholder=""
                    label="N° da carteirinha"
                    error={consultMessage}
                    value={
                      surgicalOrderNoticeCard?.data?.data?.card
                        ?.health_insurance?.health_card ||
                      integrationOvermindInfo?.data.data.health_card
                    }
                    disabled={true}
                    required
                    maxLength={25}
                  />

                  <SelectFieldNew
                    name="healthInsurance"
                    label="Convênio"
                    placeholder="Selecione o convênio"
                    items={healthInsurancesList()}
                    value={
                      surgicalOrderNoticeCard?.data?.data?.card
                        ?.health_insurance?.health_insurance_code
                    }
                    disabled={true}
                  />

                  <ButtonNew
                    disabled={
                      isLoading ||
                      disabledHealthInsuranceCard ||
                      !hasInsuranceCardNumber
                    }
                    onClick={createConsultEligibility}
                    size="medium"
                    type="button"
                    noPadding
                    fullWidth
                  >
                    Verificar elegibilidade
                  </ButtonNew>
                </S.PatientControlPanel>
              </S.RightContainer>
            </>
          )}
        </>
      )
    } else {
      return <LoadingModal show={true} />
    }
  }
}
